import type {
  DatabaseDuplicateFile,
  UploadFileData,
  UploadingFile,
} from '@hooks/useFileUploadNew';
import { useFileUploadNew } from '@hooks/useFileUploadNew';
import type { UploadedFileData } from '@retracedgmbh/ui-components-lib';
import {
  Button,
  FileUploadNew as FileUploadNewBase,
  Flex,
  Progress,
  Text,
} from '@retracedgmbh/ui-components-lib';
import type React from 'react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface StartedFile {
  file: File;
  uploadFileId: string;
  res?: Record<string, unknown>;
}

export interface FileUploadNewProps {
  /** Accepted file types — string array (e.g. ['.jpg', '.pdf']) or MIME record */
  acceptedFileTypes?: string[] | Record<string, string[]>;
  /** Max file size in bytes (default: 100MB) */
  acceptedMaxFileSize?: number;
  /** Allow multiple files */
  isMultiple?: boolean;
  /** Disable dropzone */
  disabled?: boolean;
  /** Alias for disabled */
  isReadOnly?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Custom loading text */
  loadingText?: string;
  /** Preview layout (default: 'list') */
  previewMode?: 'grid' | 'list';
  /** Header title */
  title?: string;
  /** Show "Download all" button */
  showDownloadAll?: boolean;
  /** Show grid/list toggle */
  showViewModeToggle?: boolean;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Show compact dropzone (horizontal strip) without built-in file previews */
  isCompact?: boolean;
  /** Hide file previews (show only dropzone) */
  isPreviewHidden?: boolean;
  /** Additional class name */
  className?: string;
  /** Called when dropped files are rejected */
  onDropRejected?: (fileRejections: unknown[]) => void;

  /** Initial files to display — used in self-managed mode */
  initialPreview?: UploadFileData[];
  /** Called when upload completes or file is deleted */
  onUploadFinish?: (files: UploadFileData[]) => void;
  /** Called during upload progress */
  onUploadProgress?: (files: UploadFileData[]) => void;
  /** Called when upload starts */
  onUploadStart?: (files: StartedFile[]) => void;
  /** Called with file names of files discarded as in-session duplicates */
  onSessionDuplicates?: (fileNames: string[]) => void;
  /** Async function that resolves which MD5 hashes already exist in the database */
  checkForDatabaseDuplicates?: (md5Hashes: string[]) => Promise<string[]>;
  /** Called with the files discarded as database duplicates (name + MD5 hash) */
  onDatabaseDuplicates?: (files: DatabaseDuplicateFile[]) => void;

  /**
   * Controlled mode: pass external files to display instead of internally managed ones.
   * When provided, you must also handle onDelete/onView/onDownload yourself.
   */
  files?: UploadedFileData[];
  /** Override delete handler (required in controlled mode) */
  onDelete?: (file: UploadedFileData) => void;
  /** Override view handler */
  onView?: (file: UploadedFileData) => void;
  /** Override download handler */
  onDownload?: (file: UploadedFileData) => void;
}

// ─── Progress UI ─────────────────────────────────────────────────────────────

const UploadProgress: React.FC<{ uploadingFiles: UploadingFile[] }> = ({
  uploadingFiles,
}) => {
  if (uploadingFiles.length === 0) return null;

  return (
    <Flex direction='column' gap='sm'>
      {uploadingFiles.map(uf => (
        <Flex key={uf.uploadFileId} alignItems='center' gap='md'>
          <Text
            size='small'
            color='secondary'
            textOverFlow='truncate'
            className='tw:max-w-[200px]'
          >
            {uf.name}
          </Text>
          <Progress value={uf.progress} className='tw:h-2 tw:flex-1' />
          <Text
            size='small'
            color='secondary'
            className='tw:w-10 tw:text-right tw:shrink-0'
          >
            {uf.progress}%
          </Text>
          {uf.fileXhrRequest && uf.progress < 100 && (
            <Button
              variant='ghost'
              size='icon'
              className='tw:text-content-secondary tw:hover:text-content-negative tw:h-6 tw:w-6'
              onClick={() => uf.fileXhrRequest?.abort()}
            >
              <i className='fa-solid fa-times' />
            </Button>
          )}
        </Flex>
      ))}
    </Flex>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 100; // 100MB

const FileUploadNew: React.FC<FileUploadNewProps> = ({
  acceptedFileTypes,
  acceptedMaxFileSize = DEFAULT_MAX_FILE_SIZE,
  isMultiple = false,
  disabled = false,
  isReadOnly = false,
  isLoading,
  loadingText,
  previewMode = 'list',
  title,
  showDownloadAll,
  showViewModeToggle,
  maxFiles,
  isCompact,
  isPreviewHidden,
  className,
  onDropRejected,
  initialPreview,
  onUploadFinish,
  onUploadProgress,
  onUploadStart,
  onSessionDuplicates,
  checkForDatabaseDuplicates,
  onDatabaseDuplicates,
  files: externalFiles,
  onDelete: externalOnDelete,
  onView: externalOnView,
  onDownload: externalOnDownload,
}) => {
  const isControlled = externalFiles !== undefined;

  const hook = useFileUploadNew({
    isMultiple,
    acceptedFileTypes,
    initialPreview,
    onUploadFinish,
    onUploadProgress,
    onUploadStart,
    onSessionDuplicates,
    checkForDatabaseDuplicates,
    onDatabaseDuplicates,
  });

  return (
    <FileUploadNewBase
      acceptedFileTypes={hook.mimeAccept}
      acceptedMaxFileSize={acceptedMaxFileSize}
      isMultiple={isMultiple}
      disabled={disabled || isReadOnly}
      isLoading={isLoading}
      loadingText={loadingText}
      onDrop={hook.handleDrop}
      onDropRejected={onDropRejected}
      previewMode={previewMode}
      files={isControlled ? externalFiles : hook.displayFiles}
      onView={externalOnView ?? hook.handleView}
      onDownload={externalOnDownload ?? hook.handleDownload}
      onDelete={isControlled ? externalOnDelete : hook.handleDelete}
      title={title}
      showDownloadAll={showDownloadAll}
      onDownloadAll={hook.handleDownloadAll}
      showViewModeToggle={showViewModeToggle}
      maxFiles={maxFiles}
      isCompact={isCompact}
      isPreviewHidden={isPreviewHidden}
      className={className}
    >
      <UploadProgress uploadingFiles={hook.uploadingFiles} />
    </FileUploadNewBase>
  );
};

export default FileUploadNew;
